import type { SlotDesign } from "src/components/greeting-card/design-data";

export const DRAFT_VERSION = 1;
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface DraftPayload {
  v: number;
  slots: SlotDesign[];
  savedAt: number;
}

function isValidSlot(s: unknown): boolean {
  return (
    s != null &&
    typeof s === "object" &&
    !Array.isArray(s) &&
    typeof (s as { slotKey?: unknown }).slotKey === "string" &&
    Array.isArray((s as { layers?: unknown }).layers)
  );
}

/** Persist the design under `key`. Silent no-op if storage is unavailable. */
export function writeDraft(key: string, slots: SlotDesign[], now: number): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const payload: DraftPayload = { v: DRAFT_VERSION, slots, savedAt: now };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* quota / private mode / serialization — ignore */
  }
}

/** Read a valid, in-TTL draft, or null (deleting stale/invalid entries). */
export function readDraft(key: string, now: number): SlotDesign[] | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftPayload>;
    const ok =
      parsed &&
      parsed.v === DRAFT_VERSION &&
      typeof parsed.savedAt === "number" &&
      now - parsed.savedAt <= DRAFT_TTL_MS &&
      Array.isArray(parsed.slots) &&
      parsed.slots.length > 0 &&
      parsed.slots.every(isValidSlot);
    if (!ok) {
      clearDraft(key);
      return null;
    }
    return parsed.slots as SlotDesign[];
  } catch {
    clearDraft(key);
    return null;
  }
}

export function clearDraft(key: string): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
